import {
  BeneficialOwnerControlType,
  CreateIndividualShareholdingPayload,
  ShareholdingType,
  useCreateShareholding,
  useOwnership,
  usePersonsInEntity,
  useStockClassList,
} from '@commenda/api';
import { Country } from '@prisma/client';
import {
  FieldWrapper,
  InputErrorMessage,
  Label,
  SingleCombobox,
} from '@commenda/kakashi';
import { OwnershipRelationshipType } from '@prisma/client';
import { useMemo, useState } from 'react';
import { Controller, useController } from 'react-hook-form';
import {
  CollapsibleModuleCard,
  FormActions,
} from '../../../form-builder/components/new-incorp-layout';
import { useZodForm } from '../../../hooks/useZodForm';
import { INDIVIDUAL_RELATIONSHIP_OPTIONS } from '../constants';
import { buildAddIndividualShareholderSchema } from '../schemas';
import { OwnershipPercentField } from './OwnershipPercentField';
import { SUBMIT_VALIDATION_TOAST } from './submitToast';
import { UltimateBeneficialOwnerControlTypeSection } from './UltimateBeneficialOwnerControlTypeSection';
import { applyOwnershipApiError } from './applyOwnershipApiError';
import { HscCheckbox } from './HscCheckbox';
import { StakeholderStockClassField } from './StakeholderStockClassField';
import { useStockClassStaging } from './useStockClassStaging';
import { useGetDilutedPercent } from './useGetDilutedPercent';
import { isCapitalOnlyLlc, useEntityOwnershipNouns } from '../ownershipNouns';
import { useHscThresholdAutoToggle } from './useHscThresholdAutoToggle';
import { useOwnershipFormDerivations } from './useOwnershipFormDerivations';
import { useUboControlTypeSync } from './useUboControlTypeSync';
import { AddPersonBottomsheet, NewPersonDraft } from './AddPersonBottomsheet';

interface AddIndividualShareholderFormProps {
  corporationId: number;
  onCancel: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  defaultHasSignificantControl?: boolean;
  // Called when the user clicks an already-shareholder row in the picker so
  // the parent can close this form and open the edit form for that person.
  // Passes the resolved `stakeholdingId` since the BE addresses edits by it.
  onEditExistingShareholder?: (stakeholdingId: string) => void;
}

type CandidatePerson = {
  directorId: number;
  firstName: string;
  lastName: string | null;
  dateOfBirth: string;
  nationality: Country;
};

export const AddIndividualShareholderForm = ({
  corporationId,
  onCancel,
  onSuccess,
  onError,
  defaultHasSignificantControl,
  onEditExistingShareholder,
}: AddIndividualShareholderFormProps) => {
  const shareholdingMutation = useCreateShareholding(corporationId);
  const nouns = useEntityOwnershipNouns(corporationId);
  // The schema needs this before the form is built.
  const { data: formStockClasses } = useStockClassList(corporationId);
  const capitalOnlyLlc = isCapitalOnlyLlc(nouns.tracksUnits, formStockClasses);

  const { data: ownership } = useOwnership(corporationId);
  const { data: entityPersonsResp } = usePersonsInEntity(corporationId);
  const entityPersons = entityPersonsResp?.persons ?? [];

  const [cardOpen, setCardOpen] = useState(true);
  const [bottomsheetOpen, setBottomsheetOpen] = useState(false);
  // selectedDirectorId XOR stagedPerson — submit hands one to the BE.
  const [selectedDirectorId, setSelectedDirectorId] = useState<number | null>(null);
  const [stagedPerson, setStagedPerson] = useState<NewPersonDraft | null>(null);
  const [pickerError, setPickerError] = useState<string | undefined>(undefined);

  const { percentScale, percentStep, getBudget, uboThreshold } =
    useOwnershipFormDerivations(corporationId);
  const schema = useMemo(
    () => buildAddIndividualShareholderSchema(percentScale, getBudget, nouns.classFieldNouns.singular, !capitalOnlyLlc),
    [percentScale, getBudget, nouns.classFieldNouns.singular, capitalOnlyLlc],
  );

  const {
    handleSubmit,
    control,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useZodForm({
    schema,
    defaultValues: {
      ...(defaultHasSignificantControl ? { hasSignificantControl: true } : {}),
    },
  });
  const {
    stagedStockClass,
    setStagedStockClass,
    existingStockClassId,
    selectedStockClassName,
    stockClassBody,
    ownedSharesFromPercent,
  } = useStockClassStaging(corporationId, watch('stockClassId'));

  const { field: hscField } = useController({
    control,
    name: 'hasSignificantControl',
  });
  const hsc = (hscField.value as boolean | undefined) ?? false;

  const getDilutedPercent = useGetDilutedPercent(corporationId, {
    stagedAuthorizedShares: stagedStockClass?.authorizedShares ?? null,
  });
  const { aboveThreshold } = useHscThresholdAutoToggle({
    control,
    uboThresholdPercent: uboThreshold,
    getDilutedPercent,
    stockClassId: existingStockClassId ?? null,
  });

  const ubo = useUboControlTypeSync({ hsc, aboveThreshold });

  // directorId → stakeholdingId for individuals already on the cap table —
  // used to (a) hide them from the picker, and (b) resolve a re-pick into
  // the right `stakeholdingId` for the edit-route handoff.
  const existingShareholderStakeholdingByDirectorId = useMemo(
    () =>
      new Map(
        (ownership?.shareholdings ?? [])
          .filter((s) => s.type === ShareholdingType.INDIVIDUAL && s.directorId !== null)
          .map((s) => [s.directorId as number, s.stakeholdingId]),
      ),
    [ownership],
  );
  const existingShareholderIds = useMemo(
    () => new Set(existingShareholderStakeholdingByDirectorId.keys()),
    [existingShareholderStakeholdingByDirectorId],
  );

  // Already-shareholder directors are hidden from the picker — the user
  // shouldn't be able to re-add someone who's already on the cap table.
  // `onEditExistingShareholder` stays as a defence-in-depth fallback for
  // any non-dropdown path that might land on an existing director id.
  const candidates: CandidatePerson[] = useMemo(
    () =>
      entityPersons
        .filter((p) => !existingShareholderIds.has(p.id))
        .map((p) => ({
          directorId: p.id,
          firstName: p.firstName,
          lastName: p.lastName || null,
          dateOfBirth: p.dateOfBirth,
          nationality: (p.nationalities ?? [])[0] ?? Country.US,
        })),
    [entityPersons, existingShareholderIds],
  );

  const STAGED_OPTION_VALUE = '__staged__';
  const personOptions = useMemo(() => {
    const existing = candidates.map((c) => ({
      value: String(c.directorId),
      label: `${c.firstName}${c.lastName ? ` ${c.lastName}` : ''}`,
    }));
    if (stagedPerson) {
      return [
        {
          value: STAGED_OPTION_VALUE,
          label: `${stagedPerson.firstName}${stagedPerson.lastName ? ` ${stagedPerson.lastName}` : ''}`,
        },
        ...existing,
      ];
    }
    return existing;
  }, [candidates, stagedPerson]);

  const applyExistingPerson = (c: CandidatePerson) => {
    setSelectedDirectorId(c.directorId);
    setStagedPerson(null);
    setPickerError(undefined);
    setValue('firstName', c.firstName);
    setValue('lastName', c.lastName ?? '');
    setValue('dateOfBirth', c.dateOfBirth);
    setValue('nationality', c.nationality);
  };

  const applyStagedPerson = (draft: NewPersonDraft) => {
    setSelectedDirectorId(null);
    setStagedPerson(draft);
    setPickerError(undefined);
    setValue('firstName', draft.firstName);
    setValue('lastName', draft.lastName ?? '');
    setValue('dateOfBirth', draft.dateOfBirth);
    const [primaryNationality] = draft.nationalities;
    if (primaryNationality) setValue('nationality', primaryNationality);
  };

  const handlePersonPick = (value: string) => {
    if (!value) {
      setSelectedDirectorId(null);
      setStagedPerson(null);
      return;
    }
    if (value === STAGED_OPTION_VALUE) {
      // Already on the staged row — re-applying is a no-op.
      return;
    }
    const id = Number(value);
    const existingStakeholdingId = existingShareholderStakeholdingByDirectorId.get(id);
    if (!!existingStakeholdingId) {
      onEditExistingShareholder?.(existingStakeholdingId);
      return;
    }
    const found = candidates.find((c) => c.directorId === id);
    if (!found) return;
    applyExistingPerson(found);
  };

  // Pre-submit validation collects every issue at once: each field's error
  // shows inline below it, and a single toast announces "fix the
  // highlighted fields" so the user knows where to look.
  const submit = handleSubmit(
    async (values) => {
      const nextPickerError =
        !selectedDirectorId && !stagedPerson
          ? "Pick a person or click 'Add person' to create a new one."
          : undefined;
      setPickerError(nextPickerError);
      const controlTypeOk = ubo.validate(values.hasSignificantControl === true);
      if (nextPickerError || !controlTypeOk) {
        onError?.(SUBMIT_VALIDATION_TOAST);
        return;
      }

      // Already-shareholder picks redirect, not submit.
      if (selectedDirectorId !== undefined && selectedDirectorId !== null) {
        const existingStakeholdingId =
          existingShareholderStakeholdingByDirectorId.get(selectedDirectorId);
        if (!!existingStakeholdingId) {
          onEditExistingShareholder?.(existingStakeholdingId);
          return;
        }
      }

      // Block rather than write a 0-share holding when the class has no
      // authorized count to issue the percentage against.
      const ownedShares = ownedSharesFromPercent(values.ownershipPercent);
      if (ownedShares === null) {
        setError('ownershipPercent', {
          type: 'server',
          message:
            `This ${nouns.classFieldNouns.lowerSingular} has no total set — set it before assigning a percentage.`,
        });
        onError?.(SUBMIT_VALIDATION_TOAST);
        return;
      }

      const proceed = async () => {
        try {
          // Existing-director path sends `directorId` only; identity (incl. an
          // optional/absent DOB) is derived server-side. Staged-new path sends
          // `person` from the captured draft — the Add-person sheet guarantees a
          // valid DOB there, so we read the draft rather than the (now-nullable)
          // mirrored form value. Director + Stakeholding land in one BE tx.
          const stagedNationality = stagedPerson?.nationalities[0];
          const individual: CreateIndividualShareholdingPayload = {
            ...(selectedDirectorId != null
              ? { directorId: selectedDirectorId }
              : stagedPerson && stagedNationality
                ? {
                    person: {
                      firstName: stagedPerson.firstName,
                      lastName: stagedPerson.lastName,
                      dateOfBirth: stagedPerson.dateOfBirth,
                      nationality: stagedNationality,
                    },
                  }
                : {}),
            ownedShares,
            relationshipType: values.relationshipType,
            hasSignificantControl: values.hasSignificantControl,
            ...stockClassBody,
            ...(values.hasSignificantControl && !!ubo.controlType
              ? { directControlType: ubo.controlType }
              : {}),
          };
          await shareholdingMutation.mutateAsync({ type: ShareholdingType.INDIVIDUAL, individual });
          onSuccess?.(`${nouns.singular} added.`);
          onCancel();
        } catch (err) {
          applyOwnershipApiError(err, setError, onError);
        }
      };
      await proceed();
    },
    () => onError?.(SUBMIT_VALIDATION_TOAST),
  );

  return (
    <form onSubmit={submit} className="flex flex-col gap-2xl pb-8xl">
      <CollapsibleModuleCard
        title={`Individual ${nouns.lowerSingular} details`}
        isExpanded={cardOpen}
        onExpandedChange={setCardOpen}
      >
        <FieldWrapper>
          <Label required>Person</Label>
          <SingleCombobox
            canClearSelection={false}
            options={personOptions}
            value={
              !!selectedDirectorId
                ? String(selectedDirectorId)
                : !!stagedPerson
                  ? STAGED_OPTION_VALUE
                  : ''
            }
            setValue={handlePersonPick}
            placeholder="Select a person"
            searchPlaceholder="Search people…"
            notFoundText="No matching people on this entity."
            addItemConfig={{
              shouldAllowAdd: true,
              onAddItem: () => setBottomsheetOpen(true),
              placeholder: 'Add person',
            }}
            renderOption={({ option, onSelect }) => {
              const isStaged = option.value === STAGED_OPTION_VALUE;
              return (
                <button
                  type="button"
                  onClick={() => onSelect(option.value)}
                  className="flex w-full items-center gap-md text-left"
                >
                  <span className="txt-small-medium text-text-primary">
                    {option.label}
                  </span>
                  {isStaged && (
                    <span className="txt-extra-small-regular text-text-tertiary">
                      (New)
                    </span>
                  )}
                </button>
              );
            }}
          />
          {pickerError && <InputErrorMessage message={pickerError} />}
        </FieldWrapper>

        {/* Hidden form-state inputs driven by the picker / bottomsheet. */}
        <Controller
          control={control}
          name="firstName"
          render={({ field }) => <input type="hidden" value={field.value ?? ''} readOnly />}
        />
        <Controller
          control={control}
          name="lastName"
          render={({ field }) => <input type="hidden" value={field.value ?? ''} readOnly />}
        />
        <Controller
          control={control}
          name="dateOfBirth"
          render={({ field }) => <input type="hidden" value={field.value ?? ''} readOnly />}
        />
        <Controller
          control={control}
          name="nationality"
          render={({ field }) => <input type="hidden" value={field.value ?? ''} readOnly />}
        />

        <FieldWrapper>
          <Label required>Relationship</Label>
          <Controller
            control={control}
            name="relationshipType"
            render={({ field }) => (
              <SingleCombobox
                canClearSelection={false}
                options={INDIVIDUAL_RELATIONSHIP_OPTIONS}
                value={field.value ?? ''}
                setValue={(v) =>
                  field.onChange(v as OwnershipRelationshipType)
                }
                placeholder="Select relationship"
              />
            )}
          />
          {errors.relationshipType?.message && (
            <InputErrorMessage message={errors.relationshipType.message} />
          )}
        </FieldWrapper>

        <StakeholderStockClassField
          control={control}
          name="stockClassId"
          corporationId={corporationId}
          stagedDraft={stagedStockClass}
          setStagedDraft={setStagedStockClass}
          onError={onError}
        />

        <OwnershipPercentField
          control={control}
          name="ownershipPercent"
          step={percentStep}
          errorMessage={errors.ownershipPercent?.message}
          stockClassName={selectedStockClassName}
        />

        <HscCheckbox
          checked={hsc}
          onCheckedChange={(v) =>
            hscField.onChange(typeof v === 'boolean' ? v : false)
          }
          uboThresholdPercent={uboThreshold}
        />

        {hsc && (
          <UltimateBeneficialOwnerControlTypeSection
            value={ubo.controlType ?? undefined}
            onChange={(v) => {
              ubo.setControlType(v);
              ubo.clearError();
            }}
            error={ubo.error}
          />
        )}
      </CollapsibleModuleCard>

      <FormActions
        hideBack
        showCancel
        onCancel={onCancel}
        nextLabel={`Add ${nouns.lowerSingular}`}
        isLoading={shareholdingMutation.isPending}
      />

      <AddPersonBottomsheet
        isOpen={bottomsheetOpen}
        onClose={() => setBottomsheetOpen(false)}
        onStaged={applyStagedPerson}
        onError={onError}
      />
    </form>
  );
};
